import { useMemo, useState } from "react";
import { FiCalendar } from "react-icons/fi";
import { IoWarningOutline } from "react-icons/io5";
import { LiaUserSlashSolid } from "react-icons/lia";
import { RxCross1 } from "react-icons/rx";
import { LIVE_URL } from "../../../config/config";
import useAuthToken from "../../../hooks/useAuthToken";
import styles from "./css/ReportDetail.module.scss";
import ReportChart from "./ReportChart";

const REASON_COLORS = [
  "#403D68",
  "#407022",
  "#2A70A8",
  "#FFB703",
  "#F97316",
  "#E63946",
  "#2A9D8F",
  "#E9C46A",
  "#264653",
  "#8338EC",
];

const AVATAR_COLORS = [
  "#E63946",
  "#2A9D8F",
  "#FFB703",
  "#8338EC",
  "#F97316",
  "#2A70A8",
  "#407022",
  "#403D68",
];

const getAvatarColor = (name = "") => {
  const code = name.charCodeAt(0) || 0;
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

const ReportDetail = ({ user, onClose, onActionSuccess }) => {
  const [actionLoading, setActionLoading] = useState(null);
  const { token } = useAuthToken();

  const reasonColorMap = useMemo(() => {
    const map = {};
    (user?.reason_breakdown ?? []).forEach((r, i) => {
      map[r.reason] = REASON_COLORS[i % REASON_COLORS.length];
    });
    return map;
  }, [user?.reason_breakdown]);

  if (!user) return null;

  const {
    report_user,
    report_user_id,
    max_status,
    report_count,
    reason_breakdown = [],
    report_details = [],
  } = user;

  const getReasonColor = (reason) => reasonColorMap[reason] ?? REASON_COLORS[0];

  const statusMap = {
    1: { label: "Pending", className: styles["status-pending"] },
    2: { label: "Warning", className: styles["status-warning"] },
    3: { label: "Blocked", className: styles["status-blocked"] },
  };

  const statusInfo = statusMap[max_status] ?? {
    label: "Unknown",
    className: "",
  };
  const fullName =
    `${report_user?.firstname ?? ""} ${report_user?.lastname ?? ""}`.trim();
  const initials = (report_user?.firstname?.[0] ?? "?").toUpperCase() || "?";
  const username = report_user?.username ?? "—";

  //  Update user report status
  const updateUserReportStatus = async (status, type) => {
    try {
      setActionLoading(type);

      const response = await fetch(
        `${LIVE_URL}/admin/update-user-report-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            id: report_user_id,
            status,
          }),
        },
      );

      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      onActionSuccess?.(report_user_id, status);
    } catch (error) {
      console.error(`Failed to update status (${type}):`, error);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className={`${styles.modal_overlay}`}>
      <div className={`${styles.modal_box}`}>
        <button className={`${styles.modal_close}`} onClick={onClose}>
          <RxCross1 />
        </button>
        {/* CLOSE BUTTON */}

        <div className={`${styles.modal_inner}`}>
          {/* LEFT SIDE */}
          <div className={`${styles.modal_left}`}>
            {/* USER CARD */}
            <div className={`${styles.user_card}`}>
              <div className={`${styles.user_info}`}>
                <div className={`${styles.user_avtar_inner}`}>
                  <div className={`${styles.user_image}`}>{initials}</div>
                  <div className={`${styles.user_details}`}>
                    <h3>{fullName}</h3>
                    <p>{username}</p>
                    <span>User ID {report_user_id}</span>
                  </div>
                </div>
                <div className={`${styles.pending_btn}`}>
                  <button className={statusInfo.className}>
                    {statusInfo.label}
                  </button>
                </div>
              </div>
            </div>

            {/* CHART */}
            <div className={`${styles.chart_card}`}>
              <h3>Report Summary</h3>
              <ReportChart
                reasonBreakdown={reason_breakdown}
                totalReports={report_count}
                colorMap={reasonColorMap}
              />
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className={`${styles.modal_right}`}>
            <h3>Report Summary</h3>
            <div className={`${styles.report_list}`}>
              {report_details.map((item, index) => {
                const avatarColor = getAvatarColor(item.reporter_name ?? "");
                const avatarInitial = (
                  item.reporter_name?.[0] ?? "?"
                ).toUpperCase();
                const reasonColor = getReasonColor(item.reason);
                const formattedDate = item.created_at
                  ? new Date(item.created_at).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "—";
                return (
                  <div className={styles.report_item} key={index}>
                    <div className={styles.left_section}>
                      <div
                        className={styles.report_avatar}
                        style={{ background: avatarColor }}
                      >
                        {avatarInitial}
                      </div>

                      <div
                        className={styles.dot}
                        style={{ background: reasonColor }}
                      ></div>
                    </div>

                    <div className={styles.modal_item_body}>
                      <div className={styles.report_header}>
                        <div>
                          <h4 className={styles.report_name}>
                            Reported by {item.reporter_name}
                          </h4>
                        </div>

                        <div>
                          <p className={styles.report_date}>
                            <FiCalendar />
                            {formattedDate}
                          </p>
                        </div>
                      </div>

                      <div className={styles.reason_row}>
                        <p>
                          Reason:
                          <span
                            className={styles.reason_badge}
                            style={{
                              color: reasonColor,
                              borderColor: reasonColor,
                              background: `${reasonColor}1A`,
                            }}
                          >
                            {item.reason}
                          </span>
                        </p>
                      </div>

                      <p className={styles.report_comment}>{item.comment}</p>
                    </div>
                  </div>
                );
              })}
              {report_details.length === 0 && (
                <p className={styles.no_reports}>No report details found.</p>
              )}
            </div>

            {/* BUTTONS */}
            <div className={`${styles.actions}`}>
              <button
                className={styles.warn_btn}
                disabled={actionLoading !== null}
                onClick={() => updateUserReportStatus(2, "warn")}
              >
                <IoWarningOutline />
                {actionLoading === "warn" ? "Sending..." : "Send Warning"}
              </button>

              <button
                className={styles.block_btn}
                disabled={actionLoading !== null}
                onClick={() => updateUserReportStatus(3, "block")}
              >
                <LiaUserSlashSolid />
                {actionLoading === "block"
                  ? "Blocking..."
                  : "Permanently Block"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDetail;
